import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Carousel } from 'react-responsive-carousel';
import 'react-responsive-carousel/lib/styles/carousel.min.css'; // requires a loader
import './Projects.css';

// Import project images
import terminatedImg from '../../assets/images/terminated.webp';
import theDateHutImg from '../../assets/images/thedatehut.webp';

// Import additional project images from public folder
import ruhImg from '../../assets/images/ruh.webp';
import gzfImg from '../../assets/images/gzf.webp';
import idealsImg from '../../assets/images/ideals.webp';
import swplugImg from '../../assets/images/swplug.webp';
import londoncuppingImg from '../../assets/images/londoncupping.webp';
import cpImg from '../../assets/images/cp.webp';

const Projects = () => {
  const projects = [
    {
      id: 2,
      title: 'Legal Tech',
      category: 'web',
      image: terminatedImg,
      useRealImage: true,
      description: 'Terminated.ai is a cutting-edge legal tech platform that empowers employees with professional-grade employment law tools. It uses AI to deliver instant case assessments, compensation calculations, and automated tribunal-ready document drafting.',
      technologies: ['Next.JS', 'vector DB', 'Auth'],
      link: 'https://terminated.ai'
    },
    {
      id: 4,
      title: 'Organic Foods Ecommerce',
      category: 'web',
      image: theDateHutImg,
      useRealImage: true,
      description: 'TheDateHut.co.uk is a comprehensive WooCommerce organic foods store featuring a wide range of organic products and health foods. Built with WordPress and Elementor, it offers a seamless shopping experience with product catalogs, secure checkout and customer-friendly design.',
      technologies: ['WordPress', 'WooCommerce', 'PHP'],
      link: 'https://thedatehut.co.uk'
    },
    {
      id: 9,
      title: 'Health Clinic Training',
      category: 'web',
      image: londoncuppingImg,
      useRealImage: true,
      description: 'A comprehensive e-learning platform offering structured online courses, quizzes, student enrollment, and certificate management. Designed with a user-friendly interface, secure payments, and smooth course navigation to deliver an engaging digital learning experience.',
      technologies: ['WordPress', 'LMS'],
      link: 'https://londoncupping.com'
    }
  ];

  const additionalProjects = [
    {
      id: 11,
      title: 'CP',
      image: cpImg,
      link: 'https://cloudplug.co.uk'
    },
    {
      id: 10,
      title: 'SW Plug',
      image: swplugImg,
      link: 'https://swplug.co.uk'
    },
    {
      id: 5,
      title: 'The Ruh Collective',
      image: ruhImg,
      link: 'https://theruhcollective.org'
    },
    {
      id: 6,
      title: 'Gloucester Zakat Fund',
      image: gzfImg,
      link: 'https://gloucesterzakat.com'
    },
    {
      id: 7,
      title: 'Islamic Deals',
      image: idealsImg,
      link: 'https://islamicdeals.com'
    }
  ];

  const [centerSlidePercentage, setCenterSlidePercentage] = useState(33.33);

  useEffect(() => {
    const updateCarouselSettings = () => {
      if (window.innerWidth <= 768) { // Mobile
        setCenterSlidePercentage(100);
      } else if (window.innerWidth <= 1024) { // Tablet
        setCenterSlidePercentage(50);
      } else { // Desktop
        setCenterSlidePercentage(33.33);
      }
    };

    updateCarouselSettings(); // Set initial value
    window.addEventListener('resize', updateCarouselSettings);

    return () => {
      window.removeEventListener('resize', updateCarouselSettings);
    };
  }, []);

  return (
    <section id="projects" className="projects section">
      <div className="container">
        <motion.div
          className="section-title"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2>Selected Projects</h2>
          <p>Recent launches, platforms, and digital products delivered for growing businesses.</p>
        </motion.div>

        <motion.div
          className={`projects-grid${projects.length === 2 ? ' two-projects' : ''}`}
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, staggerChildren: 0.1 }}
        >
          {projects.map((project) => (
            <motion.div
              className="project-card"
              key={project.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <div className="project-image">
                {project.useRealImage ? (
                  <img src={project.image} alt={project.title} className="project-img" />
                ) : (
                  <div className={`placeholder-project ${project.image}`}></div>
                )}
                <div className="project-overlay">
                  <a href={project.link} className="btn project-btn" target="_blank" rel="noopener noreferrer">
                    View Project
                  </a>
                </div>
              </div>
              <div className="project-info">
                <h3>{project.title}</h3>
                <p>{project.description}</p>
                <div style={{ flex: 1 }}></div>
                <ul className="tech-list">
                  {project.technologies.map((tech, index) => (
                    <li key={index} className="tech-tag">{tech}</li>
                  ))}
                </ul>
              </div>
            </motion.div>
          ))}
        </motion.div>

        <motion.div
          className="section-title"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <h2>More Projects & Collaborations</h2>
          <p>Additional brands, organisations, and products we have supported.</p>
        </motion.div>

        <motion.div
          className="additional-projects-carousel"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <Carousel
            showArrows={true}
            showStatus={false}
            showThumbs={false}
            infiniteLoop={true}
            autoPlay={true}
            interval={3000}
            centerMode={true}
            centerSlidePercentage={centerSlidePercentage}
            onClickItem={(index, item) => {
              const link = additionalProjects[index]?.link;
              if (link) {
                window.open(link, '_blank');
              }
            }}
          >
            {additionalProjects.map((project) => (
              <div key={project.id} className="carousel-item">
                <img src={project.image} alt={project.title} className="carousel-image" />
              </div>
            ))}
          </Carousel>
        </motion.div>

        <div className="projects-cta">
          <Link to="/contact" className="btn primary-btn">Let's Work Together</Link>
        </div>
      </div>
    </section>
  );
};

export default Projects;
